import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { CodeBlock } from "@/components/ui/console";
import { buttonVariants } from "@/components/ui/button";
import { toast } from "@/components/ui/toast";
import { DEV_ENVIRONMENT, DEV_API_ACTIVITY, DEV_API_HEALTH, DEV_QUICK_START_CODE } from "@/data/mock";

/* DEV-00 — Developer Home.
   Canonical source: prototype/pages/developer/index.html (no current
   Figma frame located). See the "Developer (DEV-00..02)" header
   comment in data/mock.ts for the 4 documented deviations from the
   canonical markup (retired wallet id substitution, mock-secret
   generation on DEV-01, omitted duplicate env badge, and the inert
   external-doc quicknav cards implemented below). */

const QUICKNAV = [
  { icon: "🔑", title: "API Keys", desc: "Manage API credentials and scopes", href: "/developer/api-keys" },
  { icon: "⚡", title: "Webhooks", desc: "Configure event delivery endpoints", href: "/developer/webhooks" },
];

const EXTERNAL_DOCS = [
  { icon: "📦", title: "SDK Docs ↗", desc: "@alloy/sdk reference and guides" },
  { icon: "📖", title: "API Reference ↗", desc: "OpenAPI spec and endpoint docs" },
];

export function DeveloperHome() {
  function openExternalDocs(title: string) {
    toast({ variant: "info", title: `${title.replace(" ↗", "")} — external documentation, not available in this build` });
  }

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Developers</h1>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        {QUICKNAV.map((q) => (
          <Link key={q.title} to={q.href} className="flex flex-col gap-2 rounded-xl border border-line bg-surface p-5 transition-shadow hover:border-primary hover:shadow-md">
            <div className="text-[22px]">{q.icon}</div>
            <div className="text-sm font-bold text-ink">{q.title}</div>
            <div className="text-xs text-muted">{q.desc}</div>
          </Link>
        ))}
        {EXTERNAL_DOCS.map((q) => (
          <button
            key={q.title}
            type="button"
            onClick={() => openExternalDocs(q.title)}
            className="flex flex-col gap-2 rounded-xl border border-line bg-surface p-5 text-left transition-shadow hover:border-primary hover:shadow-md"
          >
            <div className="text-[22px]">{q.icon}</div>
            <div className="text-sm font-bold text-ink">{q.title}</div>
            <div className="text-xs text-muted">{q.desc}</div>
          </button>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_340px]">
        <div>
          <div className="mb-4 rounded-xl border border-line bg-surface p-5">
            <div className="mb-3.5 text-sm font-bold text-ink">Environment</div>
            <dl className="space-y-0">
              <EnvRow k="Current Environment"><Badge variant="warning" size="sm">{DEV_ENVIRONMENT.environment}</Badge></EnvRow>
              <EnvRow k="Base URL"><span className="font-mono text-xs text-primary">{DEV_ENVIRONMENT.baseUrl}</span></EnvRow>
              <EnvRow k="API Version"><span className="font-medium text-ink">{DEV_ENVIRONMENT.apiVersion}</span></EnvRow>
              <EnvRow k="Rate Limit"><span className="font-medium text-ink">{DEV_ENVIRONMENT.rateLimit}</span></EnvRow>
              <EnvRow k="Org ID"><span className="font-mono text-xs text-ink">{DEV_ENVIRONMENT.orgId}</span></EnvRow>
            </dl>
          </div>

          <div className="overflow-hidden rounded-xl border border-line bg-surface">
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <span className="text-sm font-bold text-ink">Recent API Activity</span>
              <span className="text-xs text-muted">Last 24 hours</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-muted">
                    <th className="px-5 py-2.5">Method</th>
                    <th className="px-3 py-2.5">Endpoint</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Latency</th>
                    <th className="px-5 py-2.5">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {DEV_API_ACTIVITY.map((r, i) => (
                    <tr key={i}>
                      <td className="px-5 py-2.5">
                        <Badge variant={r.method === "GET" ? "info" : "success"} size="sm" dot={false}>{r.method}</Badge>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-ink">{r.endpoint}</td>
                      <td className={`px-3 py-2.5 font-bold ${r.ok ? "text-success" : "text-danger"}`}>{r.status}</td>
                      <td className="px-3 py-2.5 text-muted">{r.latencyMs}ms</td>
                      <td className="px-5 py-2.5 text-muted">{r.timestamp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-4 rounded-xl border border-line bg-surface p-5">
            <div className="mb-3.5 text-sm font-bold text-ink">Quick Start</div>
            <CodeBlock code={DEV_QUICK_START_CODE} className="mb-4" />
            <Link to="/developer/api-keys" className={buttonVariants({ variant: "secondary", className: "mb-2 w-full justify-center" })}>
              Manage API Keys →
            </Link>
            <Link to="/developer/webhooks" className={buttonVariants({ variant: "secondary", className: "w-full justify-center" })}>
              Configure Webhooks →
            </Link>
          </div>

          <div className="rounded-xl border border-line bg-surface p-5">
            <div className="mb-3 text-sm font-bold text-ink">API Health</div>
            <div className="flex flex-col gap-2">
              {DEV_API_HEALTH.map((h) => (
                <div key={h.name} className="flex items-center justify-between text-sm">
                  <span className="text-ink">{h.name}</span>
                  <Badge variant="success" size="sm">{h.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EnvRow({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-line py-2 text-sm last:border-0">
      <span className="text-muted">{k}</span>
      {children}
    </div>
  );
}
